/**
 * Sign in with Apple — native (iOS) helpers.
 *
 * On iOS we use the native Apple sign-in sheet (expo-apple-authentication) and exchange
 * the returned identity token with Supabase via `signInWithIdToken`. On web (non-Android) the
 * caller falls back to Supabase's OAuth redirect flow (`signInWithOAuth({ provider: 'apple' })`).
 * The Continue with Apple button is hidden on Android (native and mobile web).
 *
 * `expo-apple-authentication` is imported lazily (dynamic import) so the native module is
 * never loaded on web/Android or in unit tests — only on iOS, inside the guarded code path.
 */

import { Platform } from 'react-native';
import { isAppleSignInUiSupportedFromContext } from './appleSignInUi.helper';

/** Thrown (as Error.message) when the user dismisses the native Apple sheet. */
export const APPLE_SIGN_IN_CANCELED = 'APPLE_SIGN_IN_CANCELED';

export { isAppleSignInUiSupportedFromContext } from './appleSignInUi.helper';

/** expo-apple-authentication cancel code (user tapped Cancel on the native sheet). */
const APPLE_REQUEST_CANCELED_CODE = 'ERR_REQUEST_CANCELED';

/** Sync check for sign-in UI and auth guard (native Android + Android mobile web). */
export function isAppleSignInUiSupported(): boolean {
  if (Platform.OS === 'web') {
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    return isAppleSignInUiSupportedFromContext('web', userAgent);
  }
  return isAppleSignInUiSupportedFromContext(Platform.OS, null);
}

/**
 * True only where the native Sign in with Apple sheet is available (iOS 13+).
 * Returns false on web/Android (without importing the native module) so the caller
 * uses the OAuth redirect flow instead.
 */
export async function isAppleNativeSignInSupported(): Promise<boolean> {
  if (Platform.OS !== 'ios') return false;
  try {
    const AppleAuthentication = await import('expo-apple-authentication');
    return await AppleAuthentication.isAvailableAsync();
  } catch {
    return false;
  }
}

/**
 * Opens the native Apple sign-in sheet and returns the identity token (JWT) to exchange
 * with Supabase. Throws an Error with message `APPLE_SIGN_IN_CANCELED` when the user cancels.
 * Only call after `isAppleNativeSignInSupported()` resolves true (iOS).
 */
export async function getAppleIdentityToken(): Promise<string> {
  const AppleAuthentication = await import('expo-apple-authentication');
  try {
    const credential = await AppleAuthentication.signInAsync({
      requestedScopes: [
        AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
        AppleAuthentication.AppleAuthenticationScope.EMAIL,
      ],
    });
    if (!credential.identityToken) {
      throw new Error('Apple sign-in did not return an identity token.');
    }
    return credential.identityToken;
  } catch (error) {
    if ((error as { code?: string })?.code === APPLE_REQUEST_CANCELED_CODE) {
      throw new Error(APPLE_SIGN_IN_CANCELED);
    }
    throw error;
  }
}
